import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PASSWORD_POLICY_TEXT, getPasswordPolicyError } from '../utils/passwordPolicy';
import BrandLogo from './BrandLogo';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  // Forgot password form (no token)
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  // Reset password form (with token)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--bg-input)',
    color: 'var(--text-primary)', border: '1px solid var(--border-input)', fontSize: '14px', boxSizing: 'border-box', outline: 'none'
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setMessage(res.data?.message || 'Check your email for a reset link.');
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setSending(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return setError('Passwords do not match');
    const policyError = getPasswordPolicyError(password);
    if (policyError) return setError(policyError);
    setResetting(true);
    setError('');
    try {
      const res = await api.post('/auth/reset-password', { token, password });
      setMessage(res.data?.message || 'Password reset successfully!');
      setResetDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '40px', maxWidth: '460px', width: '100%', border: '1px solid var(--border-color)' }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <BrandLogo height={42} showBeta />
        </div>
        <h2 style={{ color: 'var(--accent)', textAlign: 'center', marginBottom: '8px' }}>
          {token ? 'Set New Password' : 'Forgot Password'}
        </h2>
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginBottom: '24px', fontSize: '14px' }}>
          {token ? 'Enter your new password below' : 'Enter your email and we\'ll send you a reset link'}
        </p>

        {error && (
          <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--danger)', color: 'var(--danger)', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
        )}
        {message && (
          <div style={{ background: 'var(--surface-soft)', border: '1px solid var(--success)', color: 'var(--success)', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>{message}</div>
        )}

        {!token ? (
          // Forgot Password Form
          sent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'📧'}</div>
              <p style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>Check your email for a password reset link.</p>
              <button onClick={() => navigate('/login')} style={{
                padding: '12px 32px', background: 'var(--border-input)', color: 'var(--text-secondary)', border: 'none',
                borderRadius: '8px', fontSize: '14px', cursor: 'pointer'
              }}>Back to Login</button>
            </div>
          ) : (
            <form onSubmit={handleForgot}>
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="forgot-email" style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Email Address</label>
                <input id="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" style={inputStyle} required />
              </div>
              <button type="submit" disabled={sending} style={{
                width: '100%', padding: '12px', background: sending ? 'var(--border-input)' : 'var(--accent)', color: 'var(--text-on-accent)',
                border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: sending ? 'not-allowed' : 'pointer'
              }}>
                {sending ? 'Sending...' : 'Send Reset Link'}
              </button>
              <p style={{ textAlign: 'center', marginTop: '16px' }}>
                <button type="button" onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '13px' }}>
                  Back to Login
                </button>
              </p>
            </form>
          )
        ) : (
          // Reset Password Form
          resetDone ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'✅'}</div>
              <p style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>Password reset successfully!</p>
              <button onClick={() => navigate('/login')} style={{
                padding: '12px 32px', background: 'var(--accent)', color: 'var(--text-on-accent)', border: 'none',
                borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer'
              }}>Go to Login</button>
            </div>
          ) : (
            <form onSubmit={handleReset}>
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="reset-new-password" style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>New Password</label>
                <input id="reset-new-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="New password" style={inputStyle} required minLength={8} autoComplete="new-password" />
                <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '6px 0 0 0' }}>{PASSWORD_POLICY_TEXT}</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="reset-confirm-password" style={{ color: 'var(--text-secondary)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>Confirm Password</label>
                <input id="reset-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password" style={inputStyle} required />
              </div>
              <button type="submit" disabled={resetting} style={{
                width: '100%', padding: '12px', background: resetting ? 'var(--border-input)' : 'var(--danger)', color: 'var(--text-on-accent)',
                border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: resetting ? 'not-allowed' : 'pointer'
              }}>
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
